/* global Liferay */
import React, { useEffect, useState } from "react";
import { Bar } from "react-chartjs-2";
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';
import axios from "axios";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const BaraemAnalytics = () => {
  const [dateRange, setDateRange] = useState({
    startDate: `${new Date().getFullYear()}-01-01`,
    endDate: new Date().toISOString().split("T")[0]
  });
  const [validationMessage, setValidationMessage] = useState("");
  const [metrics, setMetrics] = useState({ skipped: 0, scheduled: 0 });
  const [barData, setBarData] = useState([]);

  const [loading, setLoading] = useState(false);
  const [, setQuestionIds] = useState([]);
  const [analyticsData, setAnalyticsData] = useState([]);
  const [questionFilters] = useState([
  "Questions are clear and in simple language",
  "The  Questionnaire is easy to complete",
  "I find it helpful for the evaluation of my kid''s development",
  "I feel happy about implementing Dubai tool for Developmental  Screening"]
  );

  const handleStartDateChange = (e) => {
    const selectedDate = new Date(e.target.value);
    const endDate = new Date(dateRange.endDate);

    if (selectedDate <= endDate) {
      setDateRange({ ...dateRange, startDate: e.target.value });
      setValidationMessage("");
    } else {
      setValidationMessage(Liferay.Language.get('start-date-cannot-be-after-end-date'));
    }
  };

  const handleEndDateChange = (e) => {
    const selectedDate = new Date(e.target.value);
    const startDate = new Date(dateRange.startDate);

    if (selectedDate >= startDate) {
      setDateRange({ ...dateRange, endDate: e.target.value });
      setValidationMessage("");
    } else {
      setValidationMessage(Liferay.Language.get('end-date-cannot-be-before-start-date'));
    }
  };

  const fetchMetrics = async (startDate, endDate) => {
    const formattedStartDate = new Date(startDate).toISOString().split('.')[0] + 'Z';
    const endDateTime = new Date(endDate);
    endDateTime.setHours(23, 59, 59, 999);
    const formattedEndDate = endDateTime.toISOString().split('.')[0] + 'Z';

    const requestConfig = {
      url: `/o/c/tblbaraemassessmentkeymetricses?aggregationTerms=isPositiveCase,skipScheduleAppointment,scheduleAppointment&filter=dateCreated ge ${formattedStartDate} and dateCreated le ${formattedEndDate}&p_auth=${Liferay.authToken}`,
      method: 'GET',
      headers: {
        'Accept-Language': Liferay.ThemeDisplay.getLanguageId().replace('_', '-')
      }
    };
    const response = await axios.request(requestConfig);
    return response.data;
  };

  const fetchQuestionIds = async () => {
    try {
      const questionIdMap = {};
      for (const question of questionFilters) {
        const requestConfig = {
          url: `/o/c/tblquestionses/?fields=id&filter=question%20eq%20%27${encodeURIComponent(question)}%27&p_auth=${Liferay.authToken}`,
          method: 'GET',
          headers: {
            'Accept-Language': Liferay.ThemeDisplay.getLanguageId().replace('_', '-')
          }
        };
        try {
          const response = await axios.request(requestConfig);
          if (response?.data?.items && response.data.items.length > 0) {
            questionIdMap[question] = response.data.items.map((item) => item.id);
          }
        } catch (error) {
          console.error(`Error fetching question: ${question}`, error);
        }
      }
      return questionIdMap;
    } catch (error) {
      console.error("Error fetching question IDs:", error);
      return {};
    }
  };

  const fetchAnalyticsData = async (questionIdMap) => {
    if (Object.keys(questionIdMap).length === 0) return [];

    const formattedStartDate = new Date(dateRange.startDate).toISOString().split('.')[0] + 'Z';
    const endDateTime = new Date(dateRange.endDate);
    endDateTime.setHours(23, 59, 59, 999);
    const formattedEndDate = endDateTime.toISOString().split('.')[0] + 'Z';

    const analyticsResults = [];
    for (const [question, ids] of Object.entries(questionIdMap)) {
      try {
        const filterString = ids.map((id) => `questionid eq ${id}`).join(' or ');
        const requestConfig = {
          url: `/o/c/tblanswers/?aggregationTerms=answer&filter=${encodeURIComponent(`(${filterString}) and dateCreated ge ${formattedStartDate} and dateCreated le ${formattedEndDate}`)}&p_auth=${Liferay.authToken}`,
          method: 'GET',
          headers: {
            'Accept-Language': Liferay.ThemeDisplay.getLanguageId().replace('_', '-')
          }
        };
        const response = await axios.request(requestConfig);

        if (response?.data?.facets && response.data.facets.length > 0) {
          analyticsResults.push({
            questionText: question,
            facetValues: {
              'Disagree': response.data.facets[0].facetValues.find((v) => v.term === 'Disagree')?.numberOfOccurrences || 0,
              'Neutral': response.data.facets[0].facetValues.find((v) => v.term === 'Neutral')?.numberOfOccurrences || 0,
              'Agree': response.data.facets[0].facetValues.find((v) => v.term === 'Agree')?.numberOfOccurrences || 0
            }
          });
        }
      } catch (error) {
        console.error(`Error fetching analytics for question: ${question}`, error);
      }
    }
    return analyticsResults;
  };

  const loadMetrics = async () => {
    try {
      const response = await fetchMetrics(dateRange.startDate, dateRange.endDate);
      const total = response.totalCount;
      const positive = response.items.reduce((sum, item) => sum + (item.isPositiveCase ? 1 : 0), 0);

      setMetrics({ total, positive });

      // Process monthly data
      const monthlyData = {};
      response.items.forEach((item) => {
        const monthYear = new Date(item.dateCreated).toLocaleString("default", { month: "short", year: "numeric" });
        if (!monthlyData[monthYear]) {
          monthlyData[monthYear] = { total: 0, positive: 0 };
        }
        monthlyData[monthYear].total += 1;
        if (item.isPositiveCase) {
          monthlyData[monthYear].positive += 1;
        }
      });

      const startDate = new Date(dateRange.startDate);
      const endDate = new Date(dateRange.endDate);
      while (startDate <= endDate) {
        const monthYear = startDate.toLocaleString("default", { month: "short", year: "numeric" });
        if (!monthlyData[monthYear]) {
          monthlyData[monthYear] = { total: 0, positive: 0 };
        }
        startDate.setMonth(startDate.getMonth() + 1);
      }

      const sortedMonthlyData = Object.entries(monthlyData).sort((a, b) =>
      new Date(a[0]) - new Date(b[0])
      );
      setBarData(sortedMonthlyData);
    } catch (error) {
      console.error("Error loading metrics:", error);
    }
  };

  const loadAnalytics = async () => {
    setLoading(true);
    try {
      const questionIdMap = await fetchQuestionIds();
      setQuestionIds(questionIdMap);

      if (Object.keys(questionIdMap).length > 0) {
        const analytics = await fetchAnalyticsData(questionIdMap);
        setAnalyticsData(analytics);
      } else {
        setAnalyticsData([]);
      }
    } catch (error) {
      console.error("Error loading analytics:", error);
      setAnalyticsData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMetrics();
    loadAnalytics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange]);

  const processChartData = () => {
    if (!analyticsData.length) return { barData: null };

    const barData = {
      labels: analyticsData.map((item, index) => ({
        index: index + 1,
        text: item.questionText
      })),
      datasets: [
      {
        label: 'Agree',
        data: analyticsData.map((item) => item.facetValues['Agree']),
        backgroundColor: 'rgba(58, 210, 159, 0.6)', // #3AD29F (Green)
        borderColor: 'rgba(58, 210, 159, 1)',
        borderWidth: 1
      },
      {
        label: 'Neutral',
        data: analyticsData.map((item) => item.facetValues['Neutral']),
        backgroundColor: 'rgba(247, 172, 70, 0.6)', // #F7AC46 (Orange)
        borderColor: 'rgba(247, 172, 70, 1)',
        borderWidth: 1
      },
      {
        label: 'Disagree',
        data: analyticsData.map((item) => item.facetValues['Disagree']),
        backgroundColor: 'rgba(255, 106, 106, 0.6)', // #FF6A6A (Red)
        borderColor: 'rgba(255, 106, 106, 1)',
        borderWidth: 1
      }]

    };

    return { barData };
  };

  const { barData: questionBarData } = processChartData();

  const barChartOptions = {
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: { position: "top" },
      title: {
        display: true,
        text: Liferay.Language.get('response-distribution-by-question')
      },
      tooltip: {
        callbacks: {
          title: function (context) {
            const index = context[0].dataIndex;
            return questionBarData.labels[index].text;
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        title: { display: true, text: Liferay.Language.get('number-of-responses') }
      },
      x: {
        title: { display: true, text: Liferay.Language.get('questions') },
        ticks: {
          maxRotation: 0,
          autoSkip: false,
          callback: function (value, index) {
            const label = questionBarData.labels[index];
            if (label && label.text) {
              // Split the text into words and create lines of max 4 words each
              const words = label.text.split(' ');
              const lines = [];
              let currentLine = [];

              for (let i = 0; i < words.length; i++) {
                currentLine.push(words[i]);
                if (currentLine.length === 3 || i === words.length - 1) {
                  lines.push(currentLine.join(' '));
                  currentLine = [];
                }
              }

              return lines;
            }
            return value;
          }
        }
      }
    }
  };

  // Add CSS styles for the chart container
  const chartContainerStyle = {
    width: '100%',
    position: 'relative'
  };

  const cardsContent = [
  { title: Liferay.Language.get('total-count'), value: metrics.total },
  { title: Liferay.Language.get('total-positive-cases'), value: metrics.positive }];


  const metricsBarChartData = {
    labels: barData.map(([monthYear]) => monthYear),
    datasets: [
    {
      label: Liferay.Language.get('total-count'),
      data: barData.map(([, data]) => data.total),
      backgroundColor: 'rgba(60, 163, 252, 0.6)', // #3CA3FC (Blue)
      borderColor: 'rgba(60, 163, 252, 1)',
      borderWidth: 1
    },
    {
      label: Liferay.Language.get('positive-cases'),
      data: barData.map(([, data]) => data.positive),
      backgroundColor: 'rgba(172, 156, 255, 0.6)', // #AC9CFF (Purple)
      borderColor: 'rgba(172, 156, 255, 1)',
      borderWidth: 1
    }]

  };

  const metricsBarChartOptions = {
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: { position: "top" },
      title: {
        display: true,
        text: Liferay.Language.get('monthly-metrics')
      }
    },
    scales: {
      x: {
        title: { display: true, text: Liferay.Language.get('month-year') }
      },
      y: {
        beginAtZero: true,
        ticks: {
          callback: function (value) {
            if (Number.isInteger(value)) {
              return value;
            }
          }
        },
        title: { display: true, text: Liferay.Language.get('count') }
      }
    }
  };

  // Add CSS styles for the matrix table
  const matrixTableStyle = {
    backgroundColor: '#FFFFFF', // White background for table
    borderColor: '#DFE6FA' // #DFE6FA for borders
  };

  const matrixHeaderStyle = {
    backgroundColor: '#002775', // Primary Navy Blue for headers
    color: '#FFFFFF' // White text for headers
  };

  return (
    <div className="baraem-analytics-container">
            <div class="container containerFluid">

                <h1>{Liferay.Language.get('baraem-analytics')}</h1>

                <div className="filters-container">
                    <label htmlFor="startDate">{Liferay.Language.get('start-date')}</label>
                    <input
            type="date"
            id="startDate"
            value={dateRange.startDate}
            max={new Date().toISOString().split("T")[0]}
            onChange={handleStartDateChange} />
          
                    <label htmlFor="endDate">{Liferay.Language.get('end-date')}</label>
                    <input
            type="date"
            id="endDate"
            value={dateRange.endDate}
            max={new Date().toISOString().split("T")[0]}
            onChange={handleEndDateChange} />
          
                    {validationMessage &&
          <p className="validation-message" style={{ color: "red", fontSize: "14px", marginTop: "5px" }}>
                            {validationMessage}
                        </p>
          }
                    <button
            className="reset-button"
            onClick={() => {
              setDateRange({
                startDate: `${new Date().getFullYear()}-01-01`,
                endDate: new Date().toISOString().split("T")[0]
              });
              setValidationMessage("");
            }}>
            
                        {Liferay.Language.get('reset-dates')}
                    </button>
                </div>
                <div className="row">
                    <div className="col-md-5">
                        <div className="cards-container d-flex">
                            {cardsContent.map((content, index) =>
              <div key={index} className="baraem-card card mb-3">
                                    <div className="card-body">
                                        <h5 className="card-title">{content.title}</h5>
                                        <p className="card-text card-value">{content.value}</p>
                                    </div>
                                </div>
              )}
                        </div>
                    </div>
                    <div className="col-md-7">
                        {/* Monthly Metrics Chart */}
                        <div className="chart-section">
                            <div className="chart-card bar-chart card">
                                <div className="card-body" style={chartContainerStyle}>
                                    <Bar data={metricsBarChartData} options={metricsBarChartOptions} />
                                </div>
                            </div>
                        </div>
                    </div>
                    {loading ?
          <div className="loading-message">
                            <h3>{Liferay.Language.get('loading')}</h3>
                        </div> :
          analyticsData.length === 0 ?
          <div className="no-data-message">
                            <h3>{Liferay.Language.get('no-data-found')}</h3>
                        </div> :

          <>
                            <>
                                <div className="col-md-5">
                                    <div className="card" style={{ borderRadius: '10px' }}>
                                        <div className="card-body p-0">
                                            <table className="table table-bordered" style={matrixTableStyle}>
                                                <thead>
                                                    <tr>
                                                        <th style={matrixHeaderStyle}>{Liferay.Language.get('question')}</th>
                                                        <th style={matrixHeaderStyle}>{Liferay.Language.get('Agree')}</th>
                                                        <th style={matrixHeaderStyle}>{Liferay.Language.get('Neutral')}</th>
                                                        <th style={matrixHeaderStyle}>{Liferay.Language.get('Disagree')}</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {analyticsData.map((item, index) =>
                        <tr key={index}>
                                                            <td>{item.questionText}</td>
                                                            <td style={{ color: '#3AD29F' }}>{item.facetValues.Agree}</td>
                                                            <td style={{ color: '#F7AC46' }}>{item.facetValues.Neutral}</td>
                                                            <td style={{ color: '#FF6A6A' }}>{item.facetValues.Disagree}</td>
                                                        </tr>
                        )}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-7">
                                    <div className="chart-card bar-chart card">
                                        <div className="card-body" style={chartContainerStyle}>
                                            <Bar data={questionBarData} options={barChartOptions} />
                                        </div>
                                    </div>
                                </div>
                            </>
                        </>
          }
                </div>
            </div>
        </div>);



};

export default BaraemAnalytics;